import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui";
import { cn } from "~/lib/utils";

interface SectionCardProps {
  /** Lucide icon shown before the title. */
  icon: LucideIcon;
  title: ReactNode;
  children: ReactNode;
  /** Extra classes for the CardContent (e.g. `p-0` for flush tables). */
  contentClassName?: string;
  /** Capitalize the title. Pass `false` for data-derived titles (e.g. a category name). */
  capitalizeTitle?: boolean;
  /** Optional control rendered on the right side of the header (e.g. a toggle). */
  action?: ReactNode;
}

/**
 * A detail section card with an icon + title header.
 * Wraps the shadcn `Card` so every detail surface shares one header treatment.
 */
export function SectionCard({
  icon: Icon,
  title,
  children,
  contentClassName,
  capitalizeTitle = true,
  action,
}: SectionCardProps) {
  return (
    <Card>
      <CardHeader className={cn("mb-3", action && "flex flex-row items-center justify-between gap-4")}>
        <CardTitle className={cn("flex items-center gap-2", capitalizeTitle && "capitalize")}>
          <Icon className="h-4 w-4 shrink-0 text-primary-600" />
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}
